import { ChangeEvent } from "react";

import { Client } from "@/Common/Api/Client";
import { ErrorMessage } from "@/Common/Api/Dto/ErrorMessage";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { RadioButton } from "@/Common/Components/Inputs/RadioButton";
import { PatientAdvancedSearchControl } from "@/Common/Components/Search/PatientAdvancedSearchControl";
import { Patient } from "@/Common/Models/Patient/Patient";
import { Person } from "@/Common/Models/Person/Person";
import { PortalRegistrationState } from "@/Common/Models/Portal/Enums/PortalRegistrationState";
import { LinkPatientProfilePreviewResponse, LinkPatientProfileResponse } from "@/Common/Models/Portal/MedfarPortal";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { IToggleButtonFunction } from "../PortalRegistration";
import { AccountSendInvitation } from "./AccountInvitation";
import { AccountInvitationConfirmation } from "./AccountInvitationConfirmation";
import { IPortalManagementPopupErrorFunction } from "./LinkMylePatientToPortalAccountDialog";

interface IRelinkMylePatientFunction {
    (selectedPatientId: string, onAddErrorFunction: IPortalManagementPopupErrorFunction, person: Person): void
}

interface IRelinkPortalAccountPopupProps {
    onCloseCallback: (newLinkConfirmationCode: string) => void;
    componentId: string;
    patientInfo: Patient;
    patientContactId: string;
    onRelinkSuccessFunction?: IRelinkMylePatientFunction;
}

class RelinkPortalAccountPopupState {
    public isLoading: boolean;
    public relinkButtonDisabled: boolean;
    public errors: string[];

    public relinkModeOptions: KeyValuePair<string, string>[];
    public selectedRelinkModeOption: string;

    public newLinkEmail: string;
    public newLinkEmailTypeLookupId: string;
    public newLinkSendDelegate: () => void;
    public newLinkToggleSentButtonDelegate: IToggleButtonFunction;
    public newLinkConfirmationCode: string;

    public searchText: string;
    public selectedPerson: Person;
    public portalRelinkAccountId: string;

    public canRelink: boolean;
    public cantRelinkErrorReason: string;
    public canTransferDocuments: boolean;
    public isTransferingExistingDocuments: boolean;
    public canTransferSharedAccount: boolean;
    public isTransferingExistingSharedAccounts: boolean;
    public canTransferPaymentCustomer: boolean;
    public isTransferingExistingPaymentCustomer: boolean;
    public canTransferSubscription: boolean;
    public isTransferingExistingSubscription: boolean;

    constructor(s?: RelinkPortalAccountPopupState) {
        if (s != null) {
            this.isLoading = s.isLoading;
            this.relinkButtonDisabled = s.relinkButtonDisabled;
            this.relinkModeOptions = s.relinkModeOptions;
            this.selectedRelinkModeOption = s.selectedRelinkModeOption;
            this.newLinkEmail = s.newLinkEmail;
            this.newLinkEmailTypeLookupId = s.newLinkEmailTypeLookupId;
            this.newLinkSendDelegate = s.newLinkSendDelegate;
            this.newLinkToggleSentButtonDelegate = s.newLinkToggleSentButtonDelegate;
            this.searchText = s.searchText;
            this.selectedPerson = s.selectedPerson;
            this.portalRelinkAccountId = s.portalRelinkAccountId;
            this.canRelink = s.canRelink;
            this.cantRelinkErrorReason = s.cantRelinkErrorReason;

            this.canTransferDocuments = s.canTransferDocuments;
            this.isTransferingExistingDocuments = s.isTransferingExistingDocuments;
            this.canTransferSharedAccount = s.canTransferSharedAccount;
            this.isTransferingExistingSharedAccounts = s.isTransferingExistingSharedAccounts;
            this.canTransferPaymentCustomer = s.canTransferPaymentCustomer;
            this.isTransferingExistingPaymentCustomer = s.isTransferingExistingPaymentCustomer;
            this.canTransferSubscription = s.canTransferSubscription;
            this.isTransferingExistingSubscription = s.isTransferingExistingSubscription;
        }
        else {
            this.searchText = "";
            this.errors = [];
            this.relinkModeOptions = [
                new KeyValuePair(cacheStore.GetString("LinkWithExistingAccountLabel"), "existingaccount"),
                new KeyValuePair(cacheStore.GetString("LinkWithNewAccountLabel"), "newaccount")
            ];
            this.selectedRelinkModeOption = "existingaccount";
        }
    }
}

/**
 * Used used in portal management to R a MYLE patient to a portal account (ie add members and such)
 */
class RelinkPortalAccountPopup extends React.Component<IRelinkPortalAccountPopupProps, RelinkPortalAccountPopupState> {
    constructor(props: IRelinkPortalAccountPopupProps) {
        super(props);

        this.state = new RelinkPortalAccountPopupState();
    }

    public componentDidMount(): void {
        overlayStore.showOverlay("RelinkPortalAccountPopup");

        const elem = $("#" + this.props.componentId);
        const cnt = document.body;
        const dragOptions = {
            containment: cnt,
            handle: ".p-header",
            stop() {
                elem.draggable("destroy");
                elem.css("height", "");
                window.setTimeout(() => {
                    elem.draggable(dragOptions);
                }, 80);
            }
        };
        elem.draggable(dragOptions);
    }

    public componentDidUpdate(): void {
        $(window).trigger("resize");
    }

    public componentWillUnmount(): void {
        overlayStore.hideOverlay("RelinkPortalAccountPopup");
    }

    public render(): JSX.Element {
        let isSubscriptionEnabled = cacheStore.portalConfiguration?.HasSubscriptions;
        let canSendPreview = this.state.selectedPerson != null || (this.state.newLinkEmail != null && this.state.newLinkEmail.length > 0);
        let displayCannotRelink: boolean = canSendPreview && this.state.canRelink == false;
        let styleRed = { paddingLeft: "20px", fontStyle: "italic", fontSize: "small", marginTop: "-5px", paddingBottom: "10px" } as React.CSSProperties;
        let hasErrors = this.state.errors.length > 0;

        return (
            <div className="patient-sync-control " id={this.props.componentId}>
                <div className="pps-header flex-container p-header">
                    <h4>{cacheStore.GetString("PortalRelinkAccountLabel")}</h4>
                    <span onClick={this.onCloseDialog.bind(this)} className="glyphicon glyphicon-remove pointer" />
                </div>

                <div className="pps-main">
                    {hasErrors && (
                        <div className="error-message">
                            <span style={{ color: "#ff2800", fontWeight: "bold" }}>{cacheStore.GetString("ExternalSearchErrorMessage1Label")}</span>
                        </div>
                    )}

                    <div className="flex-container split relink-account ">
                        <div className="account-select-panel">
                            {(this.state.newLinkConfirmationCode == null || this.state.newLinkConfirmationCode.length == 0) && (
                                <div>
                                    <RadioButton
                                        items={Immutable.List(this.state.relinkModeOptions)}
                                        radioInputGroupId="relink_option"
                                        onePerLine={true}
                                        onChange={item => this.onRelinkTypeChanged(item.Value)}
                                        selectedItem={this.state.selectedRelinkModeOption}
                                    />
                                </div>
                            )}

                            {this.state.selectedRelinkModeOption == "existingaccount" && (
                                <div className="relink-existingaccount">
                                    <div className="ps-pad">
                                        <div className="search-component flex-container form-control">
                                            <span className="glyphicon glyphicon-search" />
                                            <input type="text" value={this.state.searchText} onChange={this.onSearchTextChanged.bind(this)} placeholder={cacheStore.GetString("SearchLabel")} />
                                        </div>
                                    </div>

                                    <div className="portal-patients-results">
                                        <PatientAdvancedSearchControl
                                            componentId="_portal_mylepatientsearch"
                                            defaultTextValue={this.state.searchText}
                                            maxResultsToReturn={8}
                                            onPatientSelectCallback={this.onSelectExistingPatient.bind(this)}
                                            isFocusOnLoad={true}
                                            isRenderTextbox={false}
                                            isScrollBarActive={false}
                                            isClickOnResultDisabled={false}
                                            isAutoResize={true}
                                            isSearchingPortal={true}
                                            isIncludingInactivePatients={true}
                                            isPortalSearchIncludeAlreadyLinked={false}
                                        />
                                    </div>
                                </div>
                            )}

                            {this.state.selectedRelinkModeOption == "newaccount" && (
                                <div className="relink-newaccount">
                                    <div>
                                        {(this.state.newLinkConfirmationCode == null || this.state.newLinkConfirmationCode.length == 0 || this.state.selectedPerson != null) && (
                                            <AccountSendInvitation
                                                patientInfo={this.props.patientInfo}
                                                setDelegatesFunction={this.setDelegates.bind(this)}
                                                onToggleSendDelegate={this.toggleSendButton.bind(this)}
                                                onSaveSuccessDelegate={this.sendRegisterNewAccountSuccess.bind(this)}
                                            />
                                        )}
                                        {(this.state.newLinkConfirmationCode != null && this.state.newLinkConfirmationCode.length > 0) &&
                                            <AccountInvitationConfirmation email={this.state.newLinkEmail} registrationCode={this.state.newLinkConfirmationCode} />
                                        }
                                    </div>
                                </div>
                            )}

                        </div>

                        <div className="transfer-details-panel">
                            {(this.state.newLinkConfirmationCode == null || this.state.newLinkConfirmationCode.length == 0) && (
                                <div>
                                    {displayCannotRelink && (
                                        <div className="red-text">
                                            <strong>  {cacheStore.GetString("RelinkImpossibleLabel")} </strong>
                                        </div>
                                    )}
                                    {displayCannotRelink && this.state.cantRelinkErrorReason != null && this.state.cantRelinkErrorReason.length > 0 && (
                                        <div className="red-text" style={styleRed}>
                                            ({this.state.cantRelinkErrorReason})
                                        </div>
                                    )}

                                    <div>
                                        <CheckboxControl
                                            id="transferDocs_check"
                                            label={cacheStore.GetString("TransferExistingDocumentsLabel")}
                                            onChange={this.onTransferDocumentsChanged.bind(this)}
                                            checked={this.state.isTransferingExistingDocuments}
                                            disabled={!this.state.canTransferDocuments}

                                        />
                                        {this.state.canRelink && !this.state.canTransferDocuments && (
                                            <div className="red-text" style={styleRed}>
                                                ({cacheStore.GetString("CannotLinkAccountDocumentsLabel")})
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <CheckboxControl
                                            id="transferAccount_check"
                                            label={cacheStore.GetString("TransferExistingSharedAccountsLabel")}
                                            onChange={this.onTransferSharedAccountsChanged.bind(this)}
                                            checked={this.state.isTransferingExistingSharedAccounts}
                                            disabled={!this.state.canTransferSharedAccount}
                                        />
                                        {this.state.canRelink && !this.state.canTransferSharedAccount && (
                                            <div className="red-text" style={styleRed}>
                                                ({cacheStore.GetString("CannotLinkAccountSharedLabel")})
                                            </div>
                                        )}
                                    </div>
                                    {isSubscriptionEnabled && (
                                        <div>
                                            <CheckboxControl
                                                id="transferPaymentInfo_check"
                                                label={cacheStore.GetString("TransferExistingPaymentInfoLabel")}
                                                onChange={this.onTransferPaymentInfoChanged.bind(this)}
                                                checked={this.state.isTransferingExistingPaymentCustomer}
                                                disabled={!this.state.canTransferPaymentCustomer}
                                            />
                                            {this.state.canRelink && !this.state.canTransferPaymentCustomer && (
                                                <div className="red-text italic" style={styleRed}>
                                                    ({cacheStore.GetString("CannotLinkAccountDocumentsLabel")})
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {isSubscriptionEnabled && (
                                        <div>
                                            <CheckboxControl
                                                id="transferSubscription_check"
                                                label={cacheStore.GetString("TransferExistingSubscriptionLabel")}
                                                onChange={this.onTransferSubscriptionChanged.bind(this)}
                                                checked={this.state.isTransferingExistingSubscription}
                                                disabled={!this.state.canTransferSubscription}
                                            />
                                            {this.state.canRelink && !this.state.canTransferSubscription && (
                                                <div className="red-text" style={styleRed}>
                                                    ({cacheStore.GetString("CannotLinkAccountSubscriptionLabel")})
                                                </div>
                                            )}
                                        </div>
                                    )}

                                </div>
                            )}
                        </div>
                    </div>

                </div>
                <div className="pps-footer flex-container">
                    <button onClick={() => this.props.onCloseCallback(undefined)} className="btn btn-default push-right-5"><span>{cacheStore.GetString("CancelLabel")}</span></button>
                    <button onClick={this.onRelinkClicked.bind(this)} disabled={!this.state.canRelink || this.state.relinkButtonDisabled} className="btn btn-primary"><span>{cacheStore.GetString("RelinkLabel")}</span></button>
                </div>
            </div>
        );
    }

    // ---------------------------------------------------------------
    // ------------------------------ relink with existing account
    // ---------------------------------------------------------------

    private onSearchTextChanged(e: ChangeEvent<HTMLInputElement>): void {
        const s = new RelinkPortalAccountPopupState(this.state);
        s.searchText = e.target.value;
        this.setState(s);
    }

    private onSelectExistingPatient(selectedPerson: Person): void {
        const s = new RelinkPortalAccountPopupState(this.state);
        s.selectedPerson = selectedPerson;
        s.portalRelinkAccountId = selectedPerson.PortalProfileId;
        this.setState(s, () => {
            this.checkPortalIfCanLink();
        });
    }

    // ---------------------------------------------------------------
    // ------------------------------ relink with new account
    // ---------------------------------------------------------------

    private setDelegates(registerDelegate: () => void): void {
        var s = new RelinkPortalAccountPopupState(this.state);
        s.newLinkSendDelegate = registerDelegate;
        this.setState(s);
    }

    private toggleSendButton(isDisabled: boolean, email: string, emailLookupId: string): void {
        let self = this;
        let s = new RelinkPortalAccountPopupState(this.state);
        if (isDisabled == false) {
            s.newLinkEmail = email;
            s.newLinkEmailTypeLookupId = emailLookupId;
        }
        else {
            s.newLinkEmail = "";
            s.newLinkEmailTypeLookupId = "";
            s.errors = [];
        }

        s.canRelink = s.canRelink && !isDisabled;
        this.setState(s, () => {
            if (!isDisabled)
                self.checkPortalIfCanLink();
        });
    }

    private sendRegisterNewAccountSuccess(portalId: string): void {
        var s = new RelinkPortalAccountPopupState(this.state);
        s.portalRelinkAccountId = portalId;
        this.setState(s);
    }

    // ---------------------------------------------------------------

    private onRelinkTypeChanged(selectedType: string): void {
        let s = new RelinkPortalAccountPopupState(this.state);
        s.selectedRelinkModeOption = selectedType;
        s.portalRelinkAccountId = "";
        s.selectedPerson = null;
        s.canRelink = false;
        s.canTransferDocuments = false;
        s.isTransferingExistingDocuments = false;
        s.canTransferPaymentCustomer = false;
        s.isTransferingExistingPaymentCustomer = false;
        s.canTransferSharedAccount = false;
        s.isTransferingExistingSharedAccounts = false;
        s.canTransferSubscription = false;
        s.isTransferingExistingSubscription = false;
        s.newLinkEmail = "";
        s.cantRelinkErrorReason = "";
        this.setState(s);
    }

    private onTransferDocumentsChanged(isChecked: boolean): void {
        let s = new RelinkPortalAccountPopupState(this.state);
        s.isTransferingExistingDocuments = isChecked;
        this.setState(s);
    }

    private onTransferSharedAccountsChanged(isChecked: boolean): void {
        let s = new RelinkPortalAccountPopupState(this.state);
        s.isTransferingExistingSharedAccounts = isChecked;
        this.setState(s);
    }

    private onTransferPaymentInfoChanged(isChecked: boolean): void {
        let s = new RelinkPortalAccountPopupState(this.state);
        s.isTransferingExistingPaymentCustomer = isChecked;
        this.setState(s);
    }

    private onTransferSubscriptionChanged(isChecked: boolean): void {
        let s = new RelinkPortalAccountPopupState(this.state);
        s.isTransferingExistingSubscription = isChecked;
        this.setState(s);
    }

    private onCloseDialog(): void {
        if (this.props.onCloseCallback != null)
            this.props.onCloseCallback(this.state.newLinkConfirmationCode);
    }

    private checkPortalIfCanLink(): void {
        let self = this;

        let data = {
            MyleContactId: self.props.patientContactId,
            PortalProfileId: (self.state.selectedPerson != null ? self.state.selectedPerson.PortalProfileId : null),
            Email: self.state.newLinkEmail
        };

        if (data.PortalProfileId || data.Email) {
            let client = new Client();

            client.Post(
                ServerOperations.Server.Portal_Proxy_GetPatientProfileRelinkPreview,
                data,
                response => self.checkPortalIfCanLinkSucess(response),
                err => self.onError(err)
            );
        }
    }

    private checkPortalIfCanLinkSucess(response: any): void {
        let preview: LinkPatientProfilePreviewResponse = response.RelinkPreview;

        let s = new RelinkPortalAccountPopupState(this.state);
        s.canRelink = false;
        if (preview != null) {
            s.canRelink = preview.CanBeLinked;
            s.canTransferDocuments = preview.CanTransferDocuments;
            s.isTransferingExistingDocuments = preview.CanTransferDocuments;

            s.canTransferPaymentCustomer = preview.CanTransferPaymentCustomer;
            s.isTransferingExistingPaymentCustomer = preview.CanTransferPaymentCustomer;

            s.canTransferSubscription = preview.CanTransferSubscription;
            s.isTransferingExistingSubscription = preview.CanTransferSubscription;

            s.canTransferSharedAccount = preview.CanTransferSharedPatients;
            s.isTransferingExistingSharedAccounts = preview.CanTransferSharedPatients;

            s.cantRelinkErrorReason = preview.Reason;
        }

        this.setState(s);
    }

    private onRelinkClicked(): void {
        let self = this;

        let data = {
            MyleContactId: self.props.patientContactId,
            RelinkedPortalAccountId: (self.state.selectedPerson != null ? self.state.selectedPerson.PortalProfileId : null),
            RelinkedAccountEmail: self.state.newLinkEmail,
            RelinkedAccountEmailTypeLookupId: self.state.newLinkEmailTypeLookupId,
            TransferDocuments: self.state.canTransferDocuments && self.state.isTransferingExistingDocuments,
            TransferSharedAccounts: self.state.canTransferSharedAccount && self.state.isTransferingExistingSharedAccounts,
            TransferSubscription: self.state.canTransferSubscription && self.state.isTransferingExistingSubscription,
            TransferPaymentMethods: self.state.canTransferPaymentCustomer && self.state.isTransferingExistingPaymentCustomer
        };

        let client = new Client();
        client.Post(
            ServerOperations.Server.Portal_Proxy_Account_Relink,
            data,
            response => self.onRelinkSuccess(response),
            err => self.onError(err)
        );

        let s = new RelinkPortalAccountPopupState(this.state);
        s.isLoading = true;
        s.relinkButtonDisabled = true;
        this.setState(s);
    }

    private onRelinkSuccess(response: any): void {
        let errors = response.Errors as ErrorMessage[];

        if (errors == null || errors.length == 0) {
            let preview: LinkPatientProfileResponse = response.RelinkResult;
            let s = new RelinkPortalAccountPopupState(this.state);

            if (preview.AuthCode != null) { // a new account was created
                s.newLinkConfirmationCode = preview.AuthCode;
                s.isLoading = false;
                s.relinkButtonDisabled = true;
                this.setState(s);
            }
            else { // an existing account was reusted
                let s = new RelinkPortalAccountPopupState(this.state);
                s.isLoading = false;
                s.relinkButtonDisabled = true;
                s.newLinkConfirmationCode = "";
                this.setState(s);
                this.onCloseDialog();
            }

            window.setTimeout(() => {
                let op = new LocalEvent(LocalOperations.Portal.Portal_UpdateInfo);
                op.AddItem("patientContactId", preview.Patient.Profile.MyleId);
                op.AddItem("portalProfileId", preview.Patient.Profile.PatientProfileId);
                op.AddItem("portalStatus", (preview.Patient.Account.IsActive ? PortalRegistrationState.RegistrationComplete : PortalRegistrationState.RegistrationPending) as number);
                op.AddItem("portalOverview", preview.Patient);
                LocalEventDispatcher.dispatch(op);
            }, 10);
        }
    }

    private onError(error: string): void {
        var s = new RelinkPortalAccountPopupState();
        s.isLoading = false;
        s.errors = [error];
        s.selectedRelinkModeOption = this.state.selectedRelinkModeOption;
        this.setState(s);
    }
}

export { RelinkPortalAccountPopup };
